import { format, formatDistanceToNow, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

/**
 * Formats date for display
 */
export const formatDate = (dateString: string): string => {
  try {
    const date = parseISO(dateString);
    return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  } catch {
    return "Data inválida";
  }
};

/**
 * Formats relative date (ex: "há 2 horas")
 */
export const formatRelativeDate = (dateString: string): string => {
  try {
    const date = parseISO(dateString);
    return formatDistanceToNow(date, {
      addSuffix: true,
      locale: ptBR,
    });
  } catch {
    return "Data inválida";
  }
};

/**
 * Formats date and time
 */
export const formatDateTime = (dateString: string): string => {
  try {
    const date = parseISO(dateString);
    return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return "Data inválida";
  }
};

/**
 * Debounce function to limit execution
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const debounce = <T extends (...args: any[]) => any>(func: T, wait: number): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Formats number for display
 */
export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat("pt-BR").format(num);
};

/**
 * Calculates percentage
 */
export const calculatePercentage = (value: number, total: number): number => {
  if (total === 0) return 0;
  return Math.round((value / total) * 100);
};

/**
 * Converts a markdown text to WhatsApp format.
 * @param markdown The markdown text to be converted.
 */
export const formatMarkdownForWhatsApp = (markdown: string): string => {
  if (!markdown) return "";

  let whatsappText = markdown;

  // Titles: ### Title => *Title*
  whatsappText = whatsappText.replace(/### (.*)/g, "*$1*");

  // Bold: **text** => *text*
  // WhatsApp uses * for bold, but markdown uses **. Let's normalize to *.
  whatsappText = whatsappText.replace(/\*\*(.*?)\*\*/g, "*$1*");

  // Italic: *text* or _text_ => _text_
  // Normalize to WhatsApp underline format.
  whatsappText = whatsappText.replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, "_$1_");

  // Strikethrough: ~~text~~ => ~text~
  whatsappText = whatsappText.replace(/~~(.*?)~~/g, "~$1~");

  // Lists: - item or * item => • item
  whatsappText = whatsappText.replace(/^[ \t]*[-*] /gm, "• ");

  // Links: [text](url) => text: url
  whatsappText = whatsappText.replace(/\[(.*?)\]\((.*?)\)/g, "$1: $2");

  // Remove multiple spaces and excess line breaks
  whatsappText = whatsappText.replace(/\n{3,}/g, "\n\n");

  return whatsappText.trim();
};
